import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Crown, MapPin, Heart, MessageCircle, Phone, Eye, ArrowLeft, Check, ShieldCheck, Star, Lock, Flag } from "lucide-react";
import CompassToTarget from "@/components/CompassToTarget";
import PhotoCarousel from "@/components/PhotoCarousel";
import ReviewsSection from "@/components/ReviewsSection";
import RatingStars from "@/components/RatingStars";
import ShareButton from "@/components/ShareButton";
import TariffsSection from "@/components/TariffsSection";

export default function ProfileDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState([]);
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("falso");
  const [reportDetails, setReportDetails] = useState("");
  const [reportReasons, setReportReasons] = useState([]);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportSent, setReportSent] = useState(false);

  useEffect(() => {
    const endpoint = /-/.test(id) && id.length < 40 ? `/profiles/by-slug/${id}` : `/profiles/${id}`;
    api.get(endpoint).then((r) => {
      setP(r.data);
      api.post("/analytics/track", { profile_id: r.data.id, event: "view" }).catch(() => {});
    }).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (p) api.get(`/reviews/profile/${p.id}`).then((r) => setReviews(r.data || [])).catch(() => {});
  }, [p]);

  // SEO meta tags driven by profile data + categories
  useEffect(() => {
    if (!p) return;
    const cats = (p.categories || []).join(", ");
    const prevTitle = document.title;
    document.title = `${p.stage_name}${p.is_vip ? " VIP" : ""} · ${p.city} | PK2 Scarlett`;

    const setMeta = (name, content, isProp = false) => {
      const attr = isProp ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const desc = `${p.stage_name}, ${p.age} años en ${p.city}. ${cats ? "Categorías: " + cats + ". " : ""}${(p.bio || "").slice(0, 140)}`;
    const kw = ["pk2 scarlett", p.city, p.state, "escort premium", ...(p.categories || [])].filter(Boolean).join(", ");
    const heroImg = p.hero_photo || (p.photos || [])[0] || "";

    setMeta("description", desc);
    setMeta("keywords", kw);
    setMeta("og:title", `${p.stage_name} · ${p.city}`, true);
    setMeta("og:description", desc, true);
    if (heroImg) setMeta("og:image", heroImg, true);
    setMeta("twitter:title", `${p.stage_name} · ${p.city}`);
    setMeta("twitter:description", desc);
    if (heroImg) setMeta("twitter:image", heroImg);

    // JSON-LD structured data for richer Google results
    const ld = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": p.stage_name,
      "url": typeof window !== "undefined" ? window.location.href : "",
      "image": heroImg || undefined,
      "description": desc,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": p.city,
        "addressRegion": p.state,
        "addressCountry": "MX",
      },
      ...(p.rating_avg && p.rating_count
        ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": p.rating_avg,
              "reviewCount": p.rating_count,
            },
          }
        : {}),
    };
    let ldEl = document.getElementById("pk2-jsonld");
    if (!ldEl) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.id = "pk2-jsonld";
      document.head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify(ld);

    return () => {
      document.title = prevTitle;
      const el = document.getElementById("pk2-jsonld");
      if (el) el.remove();
    };
  }, [p]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/favorites").then((r) => setFavs((r.data || []).map((x) => x.id))).catch(() => {});
    }
  }, [user]);

  const isFav = p && favs.includes(p.id);
  const toggleFav = async () => {
    if (!user || user === false) return nav("/login");
    if (isFav) {
      await api.delete(`/favorites/${p.id}`);
      setFavs((f) => f.filter((x) => x !== p.id));
    } else {
      await api.post("/favorites", { profile_id: p.id });
      setFavs((f) => [...f, p.id]);
    }
  };

  const sendMessage = async () => {
    if (!user || user === false) return nav("/login");
    setSending(true); setError("");
    try {
      await api.post("/messages", { to_user_id: p.user_id, body: msg });
      setSent(true); setMsg("");
      setTimeout(() => { setShowMsg(false); setSent(false); }, 1600);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  if (loading) return <div className="max-w-7xl mx-auto p-12" style={{ color: "var(--text-secondary)" }}>Cargando…</div>;
  if (!p) return (
    <div className="max-w-3xl mx-auto p-12 text-center" data-testid="profile-not-found">
      <h1 className="font-serif text-4xl mb-4">Perfil no encontrado</h1>
      <Link to="/galeria" className="btn-primary">Volver a galería</Link>
    </div>
  );

  const photos = [p.hero_photo, ...(p.photos || [])].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-24 fade-up" data-testid="profile-detail">
      <div className="flex items-center justify-between mb-6 gap-3">
        <Link to="/galeria" className="btn-ghost text-xs flex items-center gap-2" data-testid="profile-back">
          <ArrowLeft size={14} strokeWidth={1.2} /> Volver
        </Link>
        <ShareButton title={p.stage_name} text={`Mira el perfil de ${p.stage_name} en PK2 Scarlett`} testid="profile-share" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Photos */}
        <div className="lg:col-span-7">
          <div className="relative" data-testid="profile-photos">
            <PhotoCarousel photos={photos} altName={p.stage_name} testid="profile-carousel" />
            {p.is_vip && (
              <div className="absolute top-6 right-6 vip-badge z-10" data-testid="profile-vip-badge">
                <Crown size={12} strokeWidth={1.5} /> VIP
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div>
            <div className="flex items-center gap-2 overline mb-2">
              <MapPin size={10} /> {p.city}, {p.state}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-light flex items-center gap-3 flex-wrap" style={{ color: "var(--text-primary)" }} data-testid="profile-name">
              {p.stage_name}
              {p.verified && (
                <span title={t("profile.verified")} data-testid="profile-verified">
                  <ShieldCheck size={24} strokeWidth={1.2} color="var(--vip-gold)" />
                </span>
              )}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-sm flex-wrap" style={{ color: "var(--text-secondary)" }}>
              <span>{p.age} {t("profile.age_unit")}</span>
              {p.height_cm && <span>· {p.height_cm} cm</span>}
              <span className="flex items-center gap-1"><Eye size={12} /> {p.views}</span>
              {p.rating_count > 0 && (
                <span className="flex items-center gap-1" data-testid="profile-rating-summary">
                  <RatingStars value={p.rating_avg || 0} size={12} testid="profile-header-stars" />
                  <span style={{ color: "var(--vip-gold)" }}>{p.rating_avg.toFixed(1)} ({p.rating_count})</span>
                </span>
              )}
            </div>
          </div>

          <div className="divider-ornament text-xs">✦</div>

          <CompassToTarget lat={p.lat} lng={p.lng} stageName={p.stage_name} />

          <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid="profile-bio">
            {p.bio || t("profile.no_description")}
          </p>

          {p.categories?.length > 0 && (
            <div data-testid="profile-categories">
              <div className="overline mb-3">{t("profile.categories")}</div>
              <div className="flex flex-wrap gap-2">
                {p.categories.map((slug) => (
                  <Link
                    key={slug}
                    to={`/galeria?category=${slug}`}
                    className="text-xs px-3 py-1.5 inline-flex items-center transition-colors"
                    style={{
                      background: "rgba(200,16,46,0.1)",
                      border: "1px solid var(--brand-primary)",
                      color: "var(--brand-primary)",
                    }}
                  >
                    #{slug.replace("_", " ")}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {p.services?.length > 0 && (
            <div>
              <div className="overline mb-3">{t("profile.services")}</div>
              <div className="flex flex-wrap gap-2">
                {p.services.map((s) => (
                  <span key={s} className="text-xs px-3 py-1.5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)" }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {p.languages?.length > 0 && (
            <div>
              <div className="overline mb-3">{t("profile.languages")}</div>
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{p.languages.join(" · ")}</div>
            </div>
          )}

          <TariffsSection profile={p} />

          {/* Actions */}
          {(() => {
            const isGuest = !user || user === false;
            const isVisitorUnpaid = user && user !== false && user.role === "visitor" && !user.registration_paid;
            const needsPaywall = isGuest || isVisitorUnpaid;

            if (needsPaywall) {
              return (
                <div
                  className="mt-6 p-5"
                  style={{
                    background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(200,16,46,0.10))",
                    border: "1px solid var(--vip-gold)",
                    borderRadius: "2px",
                  }}
                  data-testid="contact-paywall"
                >
                  <div className="overline mb-2" style={{ color: "var(--vip-gold)" }}>Contacto privado</div>
                  <h3 className="font-serif text-xl mb-2" style={{ color: "var(--text-primary)" }}>
                    {isGuest ? "Regístrate para contactar" : "Completa tu pago de acceso"}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                    {isGuest
                      ? "Crea tu cuenta de miembro por un único pago de $50 MXN y desbloquea el WhatsApp y los mensajes privados con todas las Musas."
                      : "Tu cuenta está creada pero falta el pago único de $50 MXN para activar el contacto con las Musas."}
                  </p>
                  {isGuest ? (
                    <Link to="/registro?role=visitor" className="btn-primary" data-testid="paywall-register-cta">
                      Crear cuenta y pagar $50 MXN
                    </Link>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/auth/register/pay-access", { origin_url: window.location.origin });
                          if (data?.url) window.location.href = data.url;
                        } catch (e) { /* noop, UX handled */ }
                      }}
                      className="btn-primary"
                      data-testid="paywall-pay-cta"
                    >
                      Pagar $50 MXN ahora
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div className="flex flex-wrap gap-3 mt-4">
                <button onClick={() => setShowMsg(true)} className="btn-primary" data-testid="profile-contact-btn send-message-button">
                  <MessageCircle size={14} strokeWidth={1.5} /> {t("profile.send_message")}
                </button>
                <button onClick={toggleFav} className="btn-secondary" data-testid="profile-favorite-btn favorite-button">
                  <Heart size={14} strokeWidth={1.5} fill={isFav ? "var(--brand-primary)" : "none"} />
                  {isFav ? t("profile.saved") : t("profile.favorite")}
                </button>
                {p.whatsapp && (
                  <a
                    href={`https://wa.me/${p.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    data-testid="profile-whatsapp whatsapp-button"
                    onClick={() => api.post("/analytics/track", { profile_id: p.id, event: "whatsapp_click" }).catch(()=>{})}
                  >
                    <Phone size={14} strokeWidth={1.5} /> {p.whatsapp}
                  </a>
                )}
                {user.id !== p.user_id && (
                  <button onClick={async () => {
                    setShowReport(true);
                    if (reportReasons.length === 0) {
                      try { const { data } = await api.get("/reports/reasons"); setReportReasons(data || []); } catch {/* ignore */}
                    }
                  }} className="btn-ghost" style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} data-testid="profile-report-btn">
                    <Flag size={12} strokeWidth={1.5} /> {t("profile.report")}
                  </button>
                )}
              </div>
            );
          })()}

          <p className="text-xs mt-4" style={{ color: "var(--text-muted)" }}>
            {t("profile.safety_note")}
          </p>
        </div>
      </div>

      {/* REVIEWS — new unified component */}
      <section className="mt-20" data-testid="reviews-section">
        <ReviewsSection
          entity="profile"
          entityId={p.id}
          ratingAvg={p.rating_avg || 0}
          ratingCount={p.rating_count || 0}
          onChange={() => load()}
        />
      </section>

      {/* Review modal removed — inline form in ReviewsSection */}

      {/* Message Modal */}
      {showMsg && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(8,7,8,0.85)", backdropFilter: "blur(10px)" }} data-testid="message-modal">
          <div className="w-full max-w-md p-8" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}>
            <div className="overline mb-3">{t("profile.private_contact")}</div>
            <h3 className="font-serif text-2xl mb-5" style={{ color: "var(--text-primary)" }}>{t("profile.message_modal_title", { name: p.stage_name })}</h3>
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-8" data-testid="message-sent">
                <Check size={40} strokeWidth={1} color="var(--brand-primary)" />
                <p style={{ color: "var(--text-primary)" }}>{t("profile.message_sent")}</p>
              </div>
            ) : (
              <>
                <textarea
                  className="input-field h-32 resize-none"
                  placeholder={t("profile.message_placeholder")}
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  data-testid="message-textarea message-body"
                />
                {error && <p className="text-xs mt-2" style={{ color: "#ef6d6d" }} data-testid="message-error">{error}</p>}
                <div className="flex gap-3 mt-5">
                  <button className="btn-ghost flex-1" onClick={() => setShowMsg(false)} data-testid="message-cancel">Cancelar</button>
                  <button className="btn-primary flex-1" disabled={!msg.trim() || sending} onClick={sendMessage} data-testid="message-send">
                    {sending ? "Enviando…" : "Enviar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(8,7,8,0.88)", backdropFilter: "blur(10px)" }} data-testid="report-modal">
          <div className="w-full max-w-md p-8" style={{ background: "var(--bg-surface)", border: "1px solid #ef6d6d" }}>
            <div className="overline mb-3" style={{ color: "#ef6d6d" }}>Reporte confidencial</div>
            <h3 className="font-serif text-2xl mb-5" style={{ color: "var(--text-primary)" }}>Reportar a {p.stage_name}</h3>
            {reportSent ? (
              <div className="flex flex-col items-center gap-3 py-8" data-testid="report-sent">
                <Check size={40} strokeWidth={1} color="var(--brand-primary)" />
                <p style={{ color: "var(--text-primary)" }}>Reporte enviado</p>
                <p className="text-xs text-center" style={{ color: "var(--text-secondary)" }}>
                  Nuestro equipo revisará el perfil en las próximas 24 h.
                </p>
                <button className="btn-secondary mt-3" onClick={() => { setShowReport(false); setReportSent(false); }}>Cerrar</button>
              </div>
            ) : (
              <>
                <p className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>
                  ¿Por qué reportas a esta persona? Tu reporte es anónimo para la Musa.
                </p>
                <select
                  className="input-field mb-3"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  data-testid="report-reason-select"
                >
                  {reportReasons.length === 0 ? (
                    <option value="falso">Perfil falso / fotos robadas</option>
                  ) : reportReasons.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <textarea
                  className="input-field h-24 resize-none"
                  placeholder="Cuéntanos qué pasó (opcional, max 600 caracteres)"
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value.slice(0, 600))}
                  data-testid="report-details"
                />
                {reportError && <p className="text-xs mt-2" style={{ color: "#ef6d6d" }} data-testid="report-error">{reportError}</p>}
                <div className="flex gap-3 mt-5">
                  <button className="btn-ghost flex-1" onClick={() => setShowReport(false)} data-testid="report-cancel">Cancelar</button>
                  <button className="btn-primary flex-1" disabled={reportSubmitting}
                    onClick={async () => {
                      setReportSubmitting(true); setReportError("");
                      try {
                        await api.post("/reports", { profile_id: p.id, reason: reportReason, details: reportDetails });
                        setReportSent(true);
                      } catch (e) {
                        setReportError(formatApiError(e.response?.data?.detail) || e.message);
                      } finally { setReportSubmitting(false); }
                    }}
                    data-testid="report-submit">
                    {reportSubmitting ? "Enviando…" : "Enviar reporte"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
